import React, { useEffect } from 'react';
import {
  AcademicCapIcon,
  UserGroupIcon,
  GlobeAltIcon,
  HeartIcon,
  ShieldCheckIcon,
  EyeIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { Github, Linkedin, Mail } from 'lucide-react';

const TeamMemberCard = ({ member }) => {
  const [imgError, setImgError] = React.useState(false);

  // Fallback to initial letter avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <div className="card relative overflow-hidden p-6 flex flex-col items-center text-center">
      {/* Premium Top Line Accent */}

      {imgError ? (
        <div className="w-28 h-28 rounded-full mb-6 flex items-center justify-center bg-gradient-to-br from-primary-100 to-indigo-100 text-primary-700 text-2xl font-bold border-4 border-white shadow-md ring-4 ring-primary-500/5">
          {getInitials(member.name)}
        </div>
      ) : (
        <img
          src={member.image}
          alt={member.name}
          width="112"
          height="112"
          className="w-28 h-28 rounded-full mb-6 object-cover border-4 border-white shadow-md ring-4 ring-primary-500/5"
          onError={() => setImgError(true)}
        />
      )}

      <h3 className="text-lg font-bold text-slate-800 mb-2">
        {member.name}
      </h3>

      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200/60 mb-4">
        {member.role}{member.roleShort ? ` ${member.roleShort}` : ''}
      </span>

      <p className="text-slate-600 text-sm leading-relaxed">
        {member.description}
      </p>

      {/* Social Icons */}
      {member.socials && (
        <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-slate-100 w-full">
          {member.socials.github && (
            <a
              href={member.socials.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-800 transition-colors p-1.5 rounded-lg"
              title="GitHub"
              aria-label={`${member.name} GitHub profile`}
            >
              <Github className="h-4 w-4" />
            </a>
          )}
          {member.socials.linkedin && (
            <a
              href={member.socials.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-primary-600 transition-colors p-1.5 rounded-lg"
              title="LinkedIn"
              aria-label={`${member.name} LinkedIn profile`}
            >
              <Linkedin className="h-4 w-4" />
            </a>
          )}
          {member.socials.email && (
            <a
              href={member.socials.email}
              className="text-slate-400 hover:text-red-500 transition-colors p-1.5 rounded-lg"
              title="Email"
              aria-label={`Email ${member.name}`}
            >
              <Mail className="h-4 w-4" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

import useDocumentMetadata from '../hooks/useDocumentMetadata';

const About = () => {
  useDocumentMetadata(
    'About Us - Dakhla',
    'Learn how Dakhla connects students across Pakistan with higher education institutions and simplifies university admissions.'
  );

  const features = [
    {
      icon: AcademicCapIcon,
      title: "Comprehensive Database",
      description: "Access detailed information about Pakistan's top universities, their programs, fee structures, and admission requirements all in one place."
    },
    {
      icon: UserGroupIcon,
      title: "Student-First Design",
      description: "Our platform is designed with students in mind, providing easy-to-use tools for university comparison and application tracking."
    },
    {
      icon: HeartIcon,
      title: "Free For Students",
      description: "Our platform is completely free to use, making higher education information accessible to all students regardless of their background."
    }
  ];

  const team = [
    {
      name: "Nauman Arif",
      role: "Chief Executive Officer",
      roleShort: "(CEO)",
      image: "/assets/images/nauman.webp",
      description: "A passionate problem-solver focusing on building intuitive technology that brings value to the people.",
      socials: {
        github: "https://github.com/naumanaarif",
        linkedin: "https://linkedin.com/in/naumanaarif",
        email: "mailto:naumanaarif.work@gmail.com"
      }
    },
    {
      name: "Ismail Dad Khan",
      role: "Chief Marketing Officer",
      roleShort: "(COO)",
      image: "/assets/images/ismail.webp",
      description: "Data Analyst with a background in marketing and e-commerce, combining analytical curiosity with operational strategy to drive growth.",
      socials: {
        github: "https://github.com/IsmailDadKhan",
        linkedin: "https://linkedin.com/in/ismaildadkhan",
        email: "mailto:ismaildadkhan@gmail.com"
      }
    },
    {
      name: "Hamza Ali Khan",
      role: "Chief Technology Officer",
      roleShort: "(CTO)",
      image: "/assets/images/hamza.webp",
      description: "AI Engineer and Data Scientist passionate about building intelligent, user-centric solutions with machine learning, generative AI, and data analytics to solve real-world problems.",
      socials: {
        github: "https://github.com/Hamza-asm",
        linkedin: "https://www.linkedin.com/in/hamza-ali-856620206",
        email: "mailto:hamzasofficial1@gmail.com"
      }
    }
  ];



  const values = [
    {
      icon: EyeIcon,
      title: "Transparency",
      description: "We believe in providing clear, accurate, and up-to-date information about all universities, fee schedules, and criteria."
    },
    {
      icon: GlobeAltIcon,
      title: "Accessibility",
      description: "Education information should be free and accessible to every single student in Pakistan, regardless of background or region."
    },
    {
      icon: LightBulbIcon,
      title: "Innovation",
      description: "We continuously evolve our platform and tools using modern tech, helping students search and compare smarter."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-100 pb-10">
      {/* Hero Section */}
      <div className="relative bg-[#1E256F] text-white py-24 overflow-hidden shadow-sm">
        {/* Subtle grid pattern overlay */}
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-4">
              About Us
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 relative z-20">

        {/* Mission Card & Features Grid */}
        <div className="space-y-8">

          {/* Mission Section (Overlaps the Hero slightly for depth) */}
          <div className="card p-8 md:p-12 text-center">
            <span className="text-xs font-bold tracking-wider text-primary-600 uppercase mb-2 block">Our Goal</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-6">
              Our Mission
            </h2>
            <p className="text-base text-slate-600 max-w-4xl mx-auto leading-relaxed">
              At Dakhla, we believe that every student deserves access to comprehensive information about higher education opportunities.
              Our mission is to bridge the information gap between students and universities, making the admission process transparent,
              accessible, and stress-free for students across Pakistan.
            </p>
          </div>

          {/* Features Grid - Hidden per user request
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {features.map((feature, index) => (
 <div
 key={index}
 className="card p-6 flex flex-col items-center text-center"
 >
 <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-primary-50 text-primary-600 mb-6 shadow-sm">
 <feature.icon className="h-7 w-7" />
 </div>
 <h3 className="text-lg font-bold text-slate-800 mb-3">
 {feature.title}
 </h3>
 <p className="text-slate-600 text-sm leading-relaxed">
 {feature.description}
 </p>
 </div>
 ))}
 </div>
 */}

          {/* Meet Our Team Section */}
          <div className="text-center py-4">
            <span className="text-xs font-bold tracking-wider text-primary-600 uppercase mb-2 block">The Creators</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4">
              Meet Our Team
            </h2>
            <p className="text-base text-slate-600 mb-6 max-w-2xl mx-auto">
              We're a passionate team of educators, developers, and student advocates working to transform higher education in Pakistan.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <TeamMemberCard key={index} member={member} />
              ))}
            </div>
          </div>

          {/* Our Story Section */}
          <div className="card p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 text-center mb-6">
              Our Story
            </h2>

            <div className="text-slate-600 space-y-5 text-base leading-relaxed max-w-4xl mx-auto text-center">
              <p>
                Dakhla was born out of personal experience. As students ourselves, we faced the overwhelming challenge
                of navigating Pakistan's complex higher education landscape. Information was scattered across multiple
                websites, admission requirements were unclear, and comparing universities was nearly impossible.
              </p>
              <p>
                We realized that thousands of students across Pakistan were facing the same challenges. Many talented
                students were missing out on opportunities simply because they didn't have access to the right information
                at the right time.
              </p>
              <p>
                That's when we decided to create Dakhla - a comprehensive platform that brings together all the information
                students need to make informed decisions about their higher education. Today, we're proud to serve thousands
                of students and continue to expand our database and features.
              </p>
            </div>
          </div>

          {/* Values Section */}
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-xs font-bold tracking-wider text-primary-600 uppercase mb-2 block">Our Core Principles</span>
              <h2 className="text-3xl font-extrabold text-slate-800">
                Our Values
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {values.map((val, index) => (
                <div
                  key={index}
                  className="card bg-gradient-to-br from-white to-slate-50/50 p-8 flex flex-col items-center text-center"
                >
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary-50 text-primary-600 mb-5 shadow-sm">
                    <val.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-3">
                    {val.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {val.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default About;